import { isDefined, isNil } from "@clipboard-health/util-ts";
import { differenceInMinutes, isAfter, isBefore } from "date-fns";

export interface ValidateBreakTimestampsParams {
  breakStartTime?: Date;
  breakEndTime?: Date;
  clockInTime: Date;
  clockOutTime: Date;
  isShiftVerified: boolean;
  isBreakSkipped: boolean;
  verifiedBreakStartTime?: Date;
  verifiedBreakEndTime?: Date;
}

export type BreakErrorType =
  | "BREAK_START_BEFORE_CLOCK_IN"
  | "BREAK_START_AFTER_CLOCK_OUT"
  | "BREAK_END_AFTER_CLOCK_OUT"
  | "REDUCES_VERIFIED_TIME";

export interface BreakValidationResult {
  isValid: boolean;
  errorType?: BreakErrorType;
  message?: string;
}

/**
 * Validates break timestamps against business rules
 *
 * Returns error states that block submission. Used across break editing flow
 * and timesheet submission.
 *
 * @param params - Validation parameters
 * @returns Validation result with isValid flag and optional error
 */
export function validateBreakTimestamps(
  params: ValidateBreakTimestampsParams
): BreakValidationResult {
  const {
    breakStartTime,
    breakEndTime,
    clockInTime,
    clockOutTime,
    isShiftVerified,
    isBreakSkipped,
    verifiedBreakStartTime,
    verifiedBreakEndTime,
  } = params;

  // Priority 1: Verified time reduction error
  const verifiedTimeError = checkVerifiedTimeReductionError(
    isShiftVerified,
    isBreakSkipped,
    breakStartTime,
    breakEndTime,
    verifiedBreakStartTime,
    verifiedBreakEndTime
  );

  if (verifiedTimeError) {
    return verifiedTimeError;
  }

  // No validation against other timestamps needed for skipped breaks
  if (isBreakSkipped || isNil(breakStartTime)) {
    return {
      isValid: true,
    };
  }

  // Priority 2: Break start before clock in
  const breakStartBeforeClockInError = checkBreakStartBeforeClockIn(breakStartTime, clockInTime);
  if (breakStartBeforeClockInError) {
    return breakStartBeforeClockInError;
  }

  // Priority 3: Break start after clock out
  const breakStartAfterClockOutError = checkBreakStartAfterClockOut(breakStartTime, clockOutTime);
  if (breakStartAfterClockOutError) {
    return breakStartAfterClockOutError;
  }

  // Priority 4: Break end after clock out
  const breakEndAfterClockOutError = checkBreakEndAfterClockOut(breakEndTime, clockOutTime);
  if (breakEndAfterClockOutError) {
    return breakEndAfterClockOutError;
  }

  // No errors - validation passed
  return {
    isValid: true,
  };
}

/**
 * Error 1: Increased break duration = reduced verified shift time
 */
function checkVerifiedTimeReductionError(
  isShiftVerified: boolean,
  isBreakSkipped: boolean,
  breakStartTime?: Date,
  breakEndTime?: Date,
  verifiedBreakStartTime?: Date,
  verifiedBreakEndTime?: Date
): BreakValidationResult | undefined {
  const hasVerifiedBreak = isDefined(verifiedBreakStartTime) && isDefined(verifiedBreakEndTime);

  const verifiedBreakDurationMinutes = hasVerifiedBreak
    ? differenceInMinutes(verifiedBreakEndTime, verifiedBreakStartTime)
    : 0;

  const breakDurationMinutes =
    isBreakSkipped || isNil(breakStartTime) || isNil(breakEndTime)
      ? 0
      : differenceInMinutes(breakEndTime, breakStartTime);

  if (breakDurationMinutes > verifiedBreakDurationMinutes && isShiftVerified) {
    return {
      isValid: false,
      errorType: "REDUCES_VERIFIED_TIME",
      message: "Break duration cannot be longer than the verified break duration.",
    };
  }

  return undefined;
}

/**
 * Error 2: Break start before clock in
 */
function checkBreakStartBeforeClockIn(
  breakStartTime: Date,
  clockInTime: Date
): BreakValidationResult | undefined {
  if (isBefore(breakStartTime, clockInTime)) {
    return {
      isValid: false,
      errorType: "BREAK_START_BEFORE_CLOCK_IN",
      message: "You cannot start your break before your clock in time.",
    };
  }

  return undefined;
}

/**
 * Error 3: Break start after clock out
 */
function checkBreakStartAfterClockOut(
  breakStartTime: Date,
  clockOutTime: Date
): BreakValidationResult | undefined {
  if (isAfter(breakStartTime, clockOutTime)) {
    return {
      isValid: false,
      errorType: "BREAK_START_AFTER_CLOCK_OUT",
      message: "You cannot start your break after your clock out time.",
    };
  }

  return undefined;
}

/**
 * Error 4: Break end after clock out
 */
function checkBreakEndAfterClockOut(
  breakEndTime: Date | undefined,
  clockOutTime: Date
): BreakValidationResult | undefined {
  if (isDefined(breakEndTime) && isAfter(breakEndTime, clockOutTime)) {
    return {
      isValid: false,
      errorType: "BREAK_END_AFTER_CLOCK_OUT",
      message: "You cannot end your break after your clock out time.",
    };
  }

  return undefined;
}
